import { useCallback } from 'react';

import type { AnalysisType } from '@/shared/types/analysis';

const STORAGE_KEY = 'lastVisitedAnalysis';

type LastVisitedData = {
  [K in AnalysisType]?: string;
};

export const useLastVisitedAnalysis = () => {
  const getLastVisited = useCallback((type: AnalysisType): string | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const data: LastVisitedData = JSON.parse(stored);
      return data[type] || null;
    } catch {
      return null;
    }
  }, []);

  const setLastVisited = useCallback((type: AnalysisType, analysisId: string) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const data: LastVisitedData = stored ? JSON.parse(stored) : {};

      data[type] = analysisId;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Ignore storage errors
    }
  }, []);

  const clearLastVisited = useCallback((type?: AnalysisType) => {
    try {
      if (type) {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const data: LastVisitedData = JSON.parse(stored);
          delete data[type];
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        }
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Ignore storage errors
    }
  }, []);

  return {
    getLastVisited,
    setLastVisited,
    clearLastVisited,
  };
};
